/**
 * ReferralPage — Referral program hub.
 *
 * Shows:
 *   - User's unique referral code with copy button
 *   - Share instructions
 *   - Referral stats (count, rewards earned, cap)
 *   - Referral history
 *   - (Future) Social media post program section
 *
 * Route: /app/referral
 * Access: All authenticated household members
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { Gift, Copy, Check, Users, Award, Share2, Loader, AlertCircle } from 'lucide-react';

interface ReferralInfo {
  referralCode: string;
  referralsThisYear: number;
  referralCap: number;
  rewardsEarned: number;
  creditBalanceCents: number;
  referrals: {
    referredEmail: string;
    referredAt: string;
    rewardApplied: boolean;
  }[];
  socialMediaProgram: {
    enabled: boolean;
    description: string;
  };
}

const ReferralPage: React.FC = () => {
  const { user } = useAuth();
  const { get } = useApi();
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchReferralInfo = useCallback(async () => {
    if (!user?.householdId) return;
    setIsLoading(true);
    setErrorMsg('');
    try {
      const data = await get(`/v1/households/${user.householdId}/referral`) as ReferralInfo;
      setInfo(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load referral info';
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [user?.householdId]);

  useEffect(() => {
    fetchReferralInfo();
  }, [fetchReferralInfo]);

  const handleCopy = async () => {
    if (!info?.referralCode) return;
    try {
      await navigator.clipboard.writeText(info.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = info.referralCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const maskEmail = (email: string): string => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const masked = local.length > 2
      ? `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}`
      : `${local[0]}*`;
    return `${masked}@${domain}`;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <h1 className="page-title">Referral Program</h1>
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-text-muted)' }}>
          <Loader size={24} className="spin" />
          <p style={{ marginTop: 8 }}>Loading referral info...</p>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="page-container">
        <h1 className="page-title">Referral Program</h1>
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-error, #DC2626)' }}>
          <AlertCircle size={24} />
          <p style={{ marginTop: 8 }}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  const remaining = (info?.referralCap ?? 10) - (info?.referralsThisYear ?? 0);

  return (
    <div className="page-container">
      <h1 className="page-title">Referral Program</h1>

      {/* Referral Code Card */}
      <div className="settings-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Gift size={20} style={{ color: 'var(--color-primary)' }} />
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Your Referral Code</h3>
        </div>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 16 }}>
          Share this code with friends and family. When they subscribe, you earn 1 month free!
        </p>

        {/* Code display + copy */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'var(--color-bg-alt, #F9FAFB)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
        }}>
          <span style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            color: 'var(--color-primary)',
            fontFamily: 'monospace',
            flex: 1,
          }}>
            {info?.referralCode}
          </span>
          <button
            onClick={handleCopy}
            className="btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              background: copied ? 'var(--color-success, #059669)' : 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
          </button>
        </div>

        {/* How it works */}
        <div style={{
          background: 'rgba(45, 106, 79, 0.05)',
          borderRadius: 8,
          padding: '12px 16px',
          fontSize: 13,
          color: 'var(--color-text-muted)',
          lineHeight: 1.6,
        }}>
          <strong style={{ color: 'var(--color-text)' }}>How it works:</strong>
          <br />
          Share your code → Your friend enters it when they subscribe → You get 1 month free ($3.99 credit).
          <br />
          Earn up to {info?.referralCap} free months per year. Credits are automatically applied to your next bill.
        </div>
      </div>

      {/* Stats Card */}
      <div className="settings-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Award size={20} style={{ color: 'var(--color-primary)' }} />
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Your Referral Stats</h3>
        </div>

        <div style={{
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: info?.referrals.length ? 20 : 0,
        }}>
          <div style={{
            flex: '1 1 140px',
            background: 'linear-gradient(135deg, rgba(45, 106, 79, 0.08), rgba(45, 106, 79, 0.03))',
            border: '1px solid rgba(45, 106, 79, 0.15)',
            borderRadius: 8,
            padding: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-primary)' }}>
              ${((info?.creditBalanceCents ?? 0) / 100).toFixed(2)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Credit Balance
            </div>
          </div>

          <div style={{
            flex: '1 1 140px',
            background: 'var(--color-bg-alt, #F9FAFB)',
            borderRadius: 8,
            padding: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-primary)' }}>
              {info?.referralsThisYear ?? 0}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Referrals This Year
            </div>
          </div>

          <div style={{
            flex: '1 1 140px',
            background: 'var(--color-bg-alt, #F9FAFB)',
            borderRadius: 8,
            padding: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-success, #059669)' }}>
              {info?.rewardsEarned ?? 0}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Free Months Earned
            </div>
          </div>

          <div style={{
            flex: '1 1 140px',
            background: 'var(--color-bg-alt, #F9FAFB)',
            borderRadius: 8,
            padding: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-text-muted)' }}>
              {remaining}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Remaining This Year
            </div>
          </div>
        </div>

        {/* Referral History */}
        {info?.referrals && info.referrals.length > 0 && (
          <div>
            <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Referral History</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {info.referrals.map((ref, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: 'var(--color-bg-alt, #F9FAFB)',
                  fontSize: 13,
                }}>
                  <span style={{ color: 'var(--color-text)' }}>{maskEmail(ref.referredEmail)}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                      {new Date(ref.referredAt).toLocaleDateString()}
                    </span>
                    {ref.rewardApplied ? (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px',
                        borderRadius: 999, background: 'rgba(5, 150, 105, 0.1)',
                        color: 'var(--color-success, #059669)',
                      }}>
                        Rewarded
                      </span>
                    ) : (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px',
                        borderRadius: 999, background: 'var(--color-bg-alt)',
                        color: 'var(--color-text-muted)',
                      }}>
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!info?.referrals || info.referrals.length === 0) && (
          <p style={{ fontSize: 14, color: 'var(--color-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
            No referrals yet. Share your code to get started!
          </p>
        )}
      </div>

      {/* Social Media Program (Future) */}
      <div className="settings-card" style={{ opacity: 0.7 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Share2 size={20} style={{ color: 'var(--color-text-muted)' }} />
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Social Media Rewards</h3>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px',
            borderRadius: 999, background: 'var(--color-bg-alt)',
            color: 'var(--color-text-muted)',
          }}>
            Coming Soon
          </span>
        </div>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: 0 }}>
          {info?.socialMediaProgram.description || 'Post about TableTryb on social media and earn free months.'}
        </p>
      </div>
    </div>
  );
};

export default ReferralPage;
